import { useState, useEffect, useCallback, useRef } from 'react';
import { api } from '../lib/api.ts';
import { ItemCard } from '../components/ItemCard.tsx';
import type { Item } from '../lib/types.ts';

interface TendersProps {
  onOpenItem?: (id: string) => void;
}

type NatureFilter = 'all' | 'services' | 'works' | 'supplies';
type ValueFilter = 'all' | 'under100k' | '100k-1m' | 'over1m';
type NoticeFilter = 'all' | 'notice' | 'award';

const NATURE_LABELS: Record<NatureFilter, string> = {
  all: 'All types',
  services: 'Services',
  works: 'Works',
  supplies: 'Supplies',
};

const VALUE_LABELS: Record<ValueFilter, string> = {
  all: 'Any value',
  under100k: '< €100k',
  '100k-1m': '€100k – €1m',
  over1m: '> €1m',
};

function getValueEur(item: Item): number {
  const m = item.key_metrics ?? {};
  return Number(m['estimated_value_eur'] ?? m['estimated-value-eur'] ?? 0);
}

function getNature(item: Item): string {
  const m = item.key_metrics ?? {};
  return String(m['contract_nature'] ?? m['contract-nature'] ?? '').toLowerCase();
}

function getAuthority(item: Item): string {
  const m = item.key_metrics ?? {};
  return String(m['contracting_authority'] ?? m['contracting-authority'] ?? item.publisher ?? '');
}

function getWinner(item: Item): string {
  return String((item.key_metrics ?? {})['winner_name'] ?? '');
}

function isAward(item: Item): boolean {
  const m = item.key_metrics ?? {};
  return String(m['notice_type'] ?? '').includes('Award') || !!m['winner_name'];
}

function valueLabel(eur: number): string {
  if (!eur) return '';
  if (eur >= 1_000_000) return `€${(eur / 1_000_000).toFixed(1)}m`;
  if (eur >= 1_000) return `€${Math.round(eur / 1_000)}k`;
  return `€${eur.toLocaleString()}`;
}

function FilterBtn({ active, onClick, children }: { active: boolean; onClick: () => void; children: React.ReactNode }) {
  return (
    <button
      onClick={onClick}
      style={{
        padding: '4px 12px',
        fontSize: 12,
        border: '1px solid',
        borderRadius: 20,
        cursor: 'pointer',
        borderColor: active ? '#2563eb' : '#ddd',
        background: active ? '#eff6ff' : '#fff',
        color: active ? '#2563eb' : '#555',
        fontWeight: active ? 600 : 400,
      }}
    >
      {children}
    </button>
  );
}

const LIMIT = 25;

export function Tenders({ onOpenItem }: TendersProps) {
  const [items, setItems] = useState<Item[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [activeQ, setActiveQ] = useState(''); // debounced, committed search
  const [nature, setNature] = useState<NatureFilter>('all');
  const [valueRange, setValueRange] = useState<ValueFilter>('all');
  const [noticeType, setNoticeType] = useState<NoticeFilter>('all');
  const [offset, setOffset] = useState(0);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const load = useCallback(async (q: string) => {
    setLoading(true);
    try {
      const params = { limit: 500, offset: 0, ...(q ? { q } : {}) };
      const [ted, ogp] = await Promise.all([
        api.items.list({ source_id: 'ted_procurement', ...params }),
        api.items.list({ source_id: 'ogp_tenders', ...params }),
      ]);
      const all = [...ted.items, ...ogp.items].sort(
        (a, b) => (b.published_at ?? b.ingested_at) - (a.published_at ?? a.ingested_at)
      );
      setItems(all);
      setTotal(ted.total + ogp.total);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(''); }, [load]);

  // Debounce search: fire server request 400ms after user stops typing
  function handleSearch(val: string) {
    setSearch(val);
    setOffset(0);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setActiveQ(val);
      load(val);
    }, 400);
  }

  // Client-side filters on already-loaded items (fast, no extra requests)
  const filtered = items.filter(item => {
    if (nature !== 'all' && getNature(item) !== nature) return false;
    if (noticeType === 'award' && !isAward(item)) return false;
    if (noticeType === 'notice' && isAward(item)) return false;
    if (valueRange !== 'all') {
      const v = getValueEur(item);
      if (valueRange === 'under100k' && v >= 100_000) return false;
      if (valueRange === '100k-1m' && (v < 100_000 || v >= 1_000_000)) return false;
      if (valueRange === 'over1m' && v < 1_000_000) return false;
    }
    return true;
  });

  const page = filtered.slice(offset, offset + LIMIT);

  const changeFilter = (fn: () => void) => { fn(); setOffset(0); };
  const awardCount = items.filter(isAward).length;
  const noticeCount = items.length - awardCount;

  // Summary stats
  const totalValue = filtered.reduce((sum, i) => sum + getValueEur(i), 0);
  const natureCounts = { services: 0, works: 0, supplies: 0, other: 0 };
  items.forEach(i => {
    const n = getNature(i);
    if (n === 'services') natureCounts.services++;
    else if (n === 'works') natureCounts.works++;
    else if (n === 'supplies') natureCounts.supplies++;
    else natureCounts.other++;
  });

  const emptyState = !loading && items.length === 0;

  return (
    <div>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16, flexWrap: 'wrap' }}>
        <h1 style={{ margin: 0, fontSize: 20 }}>Procurement Tenders</h1>
        {total > 0 && (
          <span style={{ color: '#999', fontSize: 13 }}>
            {activeQ
              ? `${filtered.length} of ${items.length} matching "${activeQ}" (${total.toLocaleString()} total in DB)`
              : `Showing latest ${items.length.toLocaleString()} of ${total.toLocaleString()} notices`}
            {totalValue > 0 && ` · ${valueLabel(totalValue)} total value`}
          </span>
        )}
        <button
          onClick={() => load(activeQ)}
          style={{ marginLeft: 'auto', fontSize: 12, padding: '4px 10px', cursor: 'pointer' }}
        >
          ↻ Refresh
        </button>
      </div>

      {/* Summary tiles — only shown when we have data */}
      {items.length > 0 && (
        <div style={{ display: 'flex', gap: 10, marginBottom: 16, flexWrap: 'wrap' }}>
          {(['services', 'works', 'supplies'] as const).map(n => (
            <div
              key={n}
              onClick={() => changeFilter(() => setNature(nature === n ? 'all' : n))}
              style={{
                padding: '8px 14px',
                border: `1px solid ${nature === n ? '#2563eb' : '#e5e7eb'}`,
                borderRadius: 8,
                background: nature === n ? '#eff6ff' : '#fafafa',
                cursor: 'pointer',
                minWidth: 100,
              }}
            >
              <div style={{ fontSize: 18, fontWeight: 700, color: nature === n ? '#2563eb' : '#111' }}>
                {natureCounts[n]}
              </div>
              <div style={{ fontSize: 11, color: '#666', textTransform: 'capitalize' }}>{n}</div>
            </div>
          ))}
        </div>
      )}

      {/* Search + filters */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 14, flexWrap: 'wrap', alignItems: 'center' }}>
        <input
          type="search"
          placeholder="Search all 19k+ notices: authority, title, winner…"
          value={search}
          onChange={e => handleSearch(e.target.value)}
          style={{ padding: '6px 10px', fontSize: 13, border: '1px solid #ddd', borderRadius: 4, width: 320 }}
        />
        <span style={{ color: '#ccc', fontSize: 12 }}>|</span>
        <FilterBtn active={noticeType === 'all'} onClick={() => changeFilter(() => setNoticeType('all'))}>All</FilterBtn>
        <FilterBtn active={noticeType === 'notice'} onClick={() => changeFilter(() => setNoticeType('notice'))}>
          Open tenders {items.length > 0 && `(${noticeCount})`}
        </FilterBtn>
        <FilterBtn active={noticeType === 'award'} onClick={() => changeFilter(() => setNoticeType('award'))}>
          Awards {items.length > 0 && `(${awardCount})`}
        </FilterBtn>
        <span style={{ color: '#ccc', fontSize: 12 }}>|</span>
        {(Object.keys(NATURE_LABELS) as NatureFilter[]).map(n => (
          <FilterBtn key={n} active={nature === n} onClick={() => changeFilter(() => setNature(n))}>
            {NATURE_LABELS[n]}
          </FilterBtn>
        ))}
        <span style={{ color: '#ccc', fontSize: 12 }}>|</span>
        {(Object.keys(VALUE_LABELS) as ValueFilter[]).map(v => (
          <FilterBtn key={v} active={valueRange === v} onClick={() => changeFilter(() => setValueRange(v))}>
            {VALUE_LABELS[v]}
          </FilterBtn>
        ))}
      </div>

      {/* Loading */}
      {loading && <p style={{ color: '#999' }}>Loading tenders…</p>}

      {/* Empty state */}
      {emptyState && (
        <div style={{ textAlign: 'center', padding: '60px 0', color: '#aaa' }}>
          <div style={{ fontSize: 32, marginBottom: 12 }}>📋</div>
          <p style={{ fontWeight: 500, color: '#666' }}>No tenders ingested yet</p>
          <p style={{ fontSize: 12, maxWidth: 400, margin: '0 auto', lineHeight: 1.6 }}>
            EU procurement notices (TED) are fetched daily at 05:00, 11:00, and 17:00 UTC.
            628 Irish notices are available — they'll appear after the next scheduled run.
          </p>
        </div>
      )}

      {/* No filter match */}
      {!loading && items.length > 0 && filtered.length === 0 && (
        <div style={{ textAlign: 'center', padding: '40px 0', color: '#aaa' }}>
          <p>No notices match these filters.</p>
          <button
            onClick={() => { setSearch(''); setNature('all'); setValueRange('all'); setNoticeType('all'); setOffset(0); }}
            style={{ fontSize: 12, padding: '4px 12px', cursor: 'pointer' }}
          >
            Clear filters
          </button>
        </div>
      )}

      {/* Results */}
      <div>
        {page.map(item => {
          const v = getValueEur(item);
          const auth = getAuthority(item);
          const nat = getNature(item);
          const winner = getWinner(item);
          const award = isAward(item);
          return (
            <div key={item.id} style={{ position: 'relative' }}>
              <div style={{ display: 'flex', gap: 6, marginBottom: 2, alignItems: 'center', flexWrap: 'wrap' }}>
                {award ? (
                  <span style={{
                    fontSize: 11, fontWeight: 700,
                    background: '#f0fdf4', color: '#166534',
                    border: '1px solid #bbf7d0', borderRadius: 4, padding: '1px 6px',
                  }}>AWARDED</span>
                ) : (
                  <span style={{
                    fontSize: 11, fontWeight: 700,
                    background: '#eff6ff', color: '#1d4ed8',
                    border: '1px solid #bfdbfe', borderRadius: 4, padding: '1px 6px',
                  }}>OPEN</span>
                )}
                {v > 0 && (
                  <span style={{
                    fontSize: 11, fontWeight: 700,
                    background: v >= 1_000_000 ? '#fef2f2' : v >= 100_000 ? '#fffbeb' : '#fafafa',
                    color: v >= 1_000_000 ? '#991b1b' : v >= 100_000 ? '#92400e' : '#555',
                    border: `1px solid ${v >= 1_000_000 ? '#fecaca' : v >= 100_000 ? '#fde68a' : '#e5e7eb'}`,
                    borderRadius: 4, padding: '1px 6px',
                  }}>
                    {valueLabel(v)}
                  </span>
                )}
                {nat && <span style={{ fontSize: 11, color: '#888', textTransform: 'capitalize' }}>{nat}</span>}
                {auth && <span style={{ fontSize: 11, color: '#555', fontStyle: 'italic' }}>{auth}</span>}
                {winner && (
                  <span style={{ fontSize: 11, color: '#166534', fontWeight: 500 }}>
                    → {winner}
                  </span>
                )}
              </div>
              <ItemCard item={item} onStatusChange={() => {}} onOpen={onOpenItem} />
            </div>
          );
        })}
      </div>

      {/* Pagination */}
      {filtered.length > LIMIT && (
        <div style={{ display: 'flex', gap: 8, marginTop: 20, alignItems: 'center' }}>
          <button disabled={offset === 0} onClick={() => setOffset(o => Math.max(0, o - LIMIT))}>← Prev</button>
          <span style={{ fontSize: 12, color: '#666' }}>
            {offset + 1}–{Math.min(offset + LIMIT, filtered.length)} of {filtered.length}
          </span>
          <button disabled={offset + LIMIT >= filtered.length} onClick={() => setOffset(o => o + LIMIT)}>Next →</button>
        </div>
      )}
    </div>
  );
}
